/**
 * RISKS-DOCUMENT-VIEWER
 * HTML display of Document Unique with sexy styling
 */

import { useAppSelector, useAppDispatch } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import establishmentStore from "@/features/adm/entities/establishment/model/establishment.store"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import { useMemo, Fragment, useRef, useState, useEffect, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { t } from "i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronLeft,
  faChevronRight,
  faHome,
  faBackward,
  faForward,
  faTimes,
  faBars,
  faDownload
} from "@fortawesome/free-solid-svg-icons"
import { motion } from "framer-motion"
import ListItem from "@/components/list-item"
import Tooltip from "@/components/tooltip"
import RiskTypeChip from "@/features/risks/entities/risk/ui/RiskTypeChip"
import RiskExportModal from "@/features/risks/entities/risk/ui/modals/RiskExportModal"
import RisksDateSelector from "@/features/risks/entities/risk/ui/RisksDateSelector"

interface RisksDocumentViewerProps {
  onClose?: () => void
  isShareMode?: boolean
}

export default function RisksDocumentViewer({ onClose, isShareMode }: RisksDocumentViewerProps) {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()

  // Sélectors
  const risks = useAppSelector(riskStore.selectors.list)
  const search = useAppSelector(riskStore.selectors.search)
  const selectedDate = useAppSelector(riskStore.selectors.selectedDate)
  const units = useAppSelector(unitStore.selectors.list)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const organization = useAppSelector(organizationStore.selectors.active)
  const session = useAppSelector((state) => state._session)

  // Load all data from public endpoints when query parameters are present
  useEffect(() => {
    const establishmentId = searchParams.get("establishmentId")
    const organizationId = searchParams.get("organizationId")

    // If query params exist, load everything from public endpoints (private mode)
    if (establishmentId && organizationId) {
      dispatch(
        riskStore.thunks.fetchPublicAndSet({
          date: selectedDate,
          establishmentId,
          organizationId
        }) as any
      )
    }
  }, [searchParams, dispatch, selectedDate])

  // Reset date to today on mount
  useEffect(() => {
    dispatch(riskStore.actions.setSelectedDate(new Date()))
  }, [dispatch])

  // Reload risks when date changes (for private mode)
  useEffect(() => {
    if (!searchParams.get("establishmentId")) {
      dispatch(riskStore.thunks.fetchValidatedAndSet(selectedDate))
    }
  }, [selectedDate, dispatch, searchParams])

  const scrollToTableTop = () => {
    tableContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    if (tableScrollRef.current) {
      animateScrollTopTo(0, 400)
      setTimeout(() => animateScrollTo(0, 500), 600)
    }
  }

  const formatDocumentDate = (date: Date | null) => {
    if (!date) return new Date().toLocaleDateString("fr-FR")
    return new Date(date).toLocaleDateString("fr-FR")
  }

  const onSearch = useCallback(
    (value: string | null) => {
      const searchValue = value?.trim() ? value.trim() : null

      const searchColumns = (organization.riskAttributes || []).map((attr) => `attributes.${attr}`)

      // Scroll to table first, like home button
      if (searchValue) {
        scrollToTableTop()
      }

      dispatch(
        riskStore.actions.search({
          value: searchValue,
          columns: searchColumns.length > 0 ? searchColumns : ["name"]
        })
      )
    },
    [dispatch, organization.riskAttributes]
  )

  // Colors
  const ACCENT = session?.accountColors?.brandPrimary || "#1a3a5c"
  const ACCENT_LIGHT = lighten(ACCENT, 0.85)

  // Utils
  function lighten(hex: string, factor: number): string {
    const c = hex.replace("#", "")
    if (c.length !== 6) return hex
    const blend = (ch: string) =>
      Math.min(255, Math.round(parseInt(ch, 16) + (255 - parseInt(ch, 16)) * factor))
        .toString(16)
        .padStart(2, "0")
    return `#${blend(c.slice(0, 2))}${blend(c.slice(2, 4))}${blend(c.slice(4, 6))}`
  }

  function getContrastColor(hex: string): string {
    const c = hex.replace("#", "")
    if (c.length !== 6) return "#1a1a2e"
    const r = parseInt(c.slice(0, 2), 16)
    const g = parseInt(c.slice(2, 4), 16)
    const b = parseInt(c.slice(4, 6), 16)
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return lum > 0.55 ? "#1a1a2e" : "white"
  }

  function scalePositionColor(indexFromTop: number, total: number): string {
    if (total <= 1) return "#999"
    const ratio = indexFromTop / (total - 1)
    if (ratio < 0.5) {
      const t = ratio * 2
      const r = Math.round(0x27 + (0xe6 - 0x27) * t)
      const g = Math.round(0xae + (0x7e - 0xae) * t)
      const b = Math.round(0x60 + (0x22 - 0x60) * t)
      return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
    } else {
      const t = (ratio - 0.5) * 2
      const r = Math.round(0xe6 + (0xc0 - 0xe6) * t)
      const g = Math.round(0x7e + (0x39 - 0x7e) * t)
      const b = Math.round(0x22 + (0x2b - 0x22) * t)
      return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
    }
  }

  // Calculate intelligent min-width for attribute columns based on content
  const getAttributeMinWidth = (attr: string, unitRisks: (typeof risks)[0][]) => {
    const maxLength = Math.max(attr.length, ...unitRisks.map((r) => (r.attributes?.[attr] || "—").toString().length))
    // ~4px per character + padding, capped at 200px
    return Math.min(200, Math.max(50, maxLength * 4 + 10))
  }

  // Highlight search matches in text
  const highlightSearchMatch = (text: string | number) => {
    if (!search.value) return text

    const textStr = String(text)
    const regex = new RegExp(`(${search.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    const parts = textStr.split(regex)

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} style={{ backgroundColor: "#fbbf24", fontWeight: "600" }}>
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  // Group risks by unit
  const byUnit = useMemo(() => {
    const map = new Map<string, typeof risks>()
    for (const risk of risks) {
      const key = risk.UnitId || "Sans unité"
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(risk)
    }
    // Sort each unit's risks by residual value desc
    for (const [, unitRisks] of map) {
      unitRisks.sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))
    }
    return map
  }, [risks])

  const ACCENT_TEXT = getContrastColor(ACCENT)

  const tableScrollRef = useRef<HTMLDivElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const mainScrollRef = useRef<HTMLDivElement>(null)
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [isScrollingLeft, setIsScrollingLeft] = useState(false)
  const [isScrollingRight, setIsScrollingRight] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  const [isUnitsMenuOpen, setIsUnitsMenuOpen] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  const startScroll = (direction: "left" | "right") => {
    if (direction === "left") setIsScrollingLeft(true)
    else setIsScrollingRight(true)
  }

  const stopScroll = () => {
    setIsScrollingLeft(false)
    setIsScrollingRight(false)
  }

  const animateScrollTo = (targetScroll: number, duration: number = 500) => {
    if (!tableScrollRef.current) return

    const start = tableScrollRef.current.scrollLeft
    const startTime = Date.now()

    const animate = () => {
      if (!tableScrollRef.current) return

      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function (ease-out cubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      tableScrollRef.current.scrollLeft = start + (targetScroll - start) * easeProgress

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        updateScrollState()
      }
    }

    requestAnimationFrame(animate)
  }

  const animateScrollTopTo = (targetScroll: number, duration: number = 500) => {
    if (!tableScrollRef.current) return

    const start = tableScrollRef.current.scrollTop
    const startTime = Date.now()

    const animate = () => {
      if (!tableScrollRef.current) return

      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function (ease-out cubic)
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      tableScrollRef.current.scrollTop = start + (targetScroll - start) * easeProgress

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }

  const updateScrollState = () => {
    if (tableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5)
    }
  }

  const scrollToUnit = (unitId: string) => {
    if (!tableScrollRef.current) return

    onSearch(null)

    // Find first risk row for this unit
    const unitRows = tableScrollRef.current.querySelectorAll(`tr[data-unit-id="${unitId}"]`)
    if (unitRows.length > 0) {
      const firstRow = unitRows[0] as HTMLElement
      const rowTop = firstRow.offsetTop
      // Account for sticky header at top: 4rem (64px)
      animateScrollTopTo(rowTop - 160, 400)
    }
    setIsUnitsMenuOpen(false)
  }

  useEffect(() => {
    const ref = tableScrollRef.current
    if (ref) {
      ref.addEventListener("scroll", updateScrollState)
      updateScrollState()
      return () => ref.removeEventListener("scroll", updateScrollState)
    }
  }, [])

  useEffect(() => {
    if ((isScrollingLeft || isScrollingRight) && tableScrollRef.current) {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current)

      scrollIntervalRef.current = setInterval(() => {
        if (tableScrollRef.current) {
          const step = 20
          if (isScrollingLeft) {
            tableScrollRef.current.scrollLeft -= step
          } else if (isScrollingRight) {
            tableScrollRef.current.scrollLeft += step
          }
          updateScrollState()
        }
      }, 50)
    } else if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }

    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
      }
    }
  }, [isScrollingLeft, isScrollingRight])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isUnitsMenuOpen) {
        const actionBar = document.querySelector("[data-action-bar]")
        if (actionBar && !actionBar.contains(event.target as Node)) {
          setIsUnitsMenuOpen(false)
        }
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [isUnitsMenuOpen])

  if (!establishment || !organization) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-2xl">
          <p>{t("Données manquantes pour afficher le document")}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-300 rounded">
            {t("Fermer")}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`w-full mx-auto my-6 overflow-y-auto p-5 ${isShareMode ? "h-full pr-6" : ""}`} ref={mainScrollRef}>
      {isExportModalOpen && <RiskExportModal onClose={() => setIsExportModalOpen(false)} />}
      {/* Intro Section */}
      <div className="rounded-lg p-6 shadow-lg" style={{ backgroundColor: ACCENT }}>
        <h1 className="text-3xl font-bold" style={{ color: "white" }}>
          {t("Document Unique")}
        </h1>
        <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.7)" }}>
          {t("Décret n° 2001-1016 du 5 Novembre 2001")}
        </p>
        <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
          {t("En date du")} {formatDocumentDate(selectedDate)}
        </p>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-6 mt-8 p-6 rounded-lg shadow-lg" style={{ backgroundColor: "#fafafa" }}>
        {/* Left: Establishment */}
        <div>
          <div className="rounded-t-lg px-6 py-3 font-bold text-white" style={{ backgroundColor: ACCENT }}>
            {t("ÉTABLISSEMENT")}
          </div>
          <div className="border border-t-0 border-gray-200">
            {[
              [t("Nom"), establishment.name],
              [t("Adresse"), establishment.address || "—"],
              [t("SIRET"), establishment.establishmentId || "—"],
              [t("Code NAF"), establishment.industryCode || "—"],
              [t("Organisation"), organization.name],
              [t("SIREN"), organization.establishmentId || "—"]
            ].map(([label, value], i) => (
              <div key={i} className="flex border-b last:border-b-0">
                <div
                  className="px-4 py-2.5 font-semibold border-r min-w-fit"
                  style={{ backgroundColor: ACCENT_LIGHT, color: ACCENT }}>
                  {label}
                </div>
                <div className="px-4 py-2.5 text-sm flex-1">{value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Stats */}
        <div>
          <div className="rounded-t-lg px-6 py-3 font-bold text-white" style={{ backgroundColor: ACCENT }}>
            {t("DOCUMENT")}
          </div>
          <div className="border border-t-0 border-gray-200">
            {[
              [t("Unités de travail"), byUnit.size.toString()],
              [t("Risques identifiés"), risks.length.toString()]
            ].map(([label, value], i) => (
              <div key={i} className="flex border-b last:border-b-0">
                <div
                  className="px-4 py-2.5 font-semibold border-r min-w-fit"
                  style={{ backgroundColor: ACCENT_LIGHT, color: ACCENT }}>
                  {label}
                </div>
                <div className="px-4 py-2.5 text-sm flex-1">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risks by Unit - Single Table */}
      <div className="mt-8 border rounded-lg overflow-hidden relative shadow-lg h-[43.5rem]" ref={tableContainerRef}>
        <div className="max-h-[41rem] relative overflow-x-hidden shadow-inner" ref={tableScrollRef}>
          <table className="text-sm border-collapse w-full" style={{ tableLayout: "auto" }}>
            <thead className="sticky top-0 z-10">
              <tr style={{ backgroundColor: ACCENT, color: ACCENT_TEXT }}>
                <th
                  className="px-1.5 py-3 text-center font-semibold border border-gray-300"
                  style={{ minWidth: "100px", maxWidth: "120px" }}>
                  {t("Type de risque")}
                </th>
                {(organization.riskAttributes || []).map((attr) => (
                  <th
                    key={attr}
                    className="px-3 py-3 text-left font-semibold border border-gray-300 break-words"
                    style={{ minWidth: `${getAttributeMinWidth(attr, risks)}px`, maxWidth: "200px" }}>
                    {attr}
                  </th>
                ))}
                <th
                  className="px-1.5 py-3 text-center font-semibold border border-gray-300 min-w-5"
                  style={{ maxWidth: "80px" }}>
                  G
                </th>
                <th
                  className="px-1.5 py-3 text-center font-semibold border border-gray-300 min-w-5"
                  style={{ maxWidth: "80px" }}>
                  F
                </th>
                <th
                  className="px-1.5 py-3 text-center font-semibold border border-gray-300 min-w-14"
                  style={{ maxWidth: "120px" }}>
                  {t("Risque brut")}
                </th>
                <th
                  className="px-1.5 py-3 text-center font-semibold border border-gray-300 min-w-5"
                  style={{ maxWidth: "80px" }}>
                  P
                </th>
                <th
                  className="px-1.5 py-3 text-center font-semibold border border-gray-300"
                  style={{ minWidth: "130px", maxWidth: "130px" }}>
                  {t("Risque résiduel")}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from(byUnit.entries()).map(([unitId, unitRisks]) => {
                const unit = units.find((u) => u.id === unitId)
                const unitName = unit?.name ?? unitId
                const unitSize = unit?.size ?? null
                const columnCount = (organization.riskAttributes || []).length + 6

                return (
                  <Fragment key={unitId}>
                    {/* Unit Section Row */}
                    <tr
                      className="sticky"
                      style={{
                        backgroundColor: ACCENT_LIGHT,
                        borderTop: `3px solid ${ACCENT}`,
                        top: "4rem",
                        zIndex: 5
                      }}>
                      <td colSpan={columnCount} className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <h3 className="text-base font-bold" style={{ color: ACCENT }}>
                            {unitName}
                          </h3>
                          <span className="text-sm text-gray-600">
                            {unitSize && `${unitSize} collab.`}
                            {unitSize && unitRisks.length > 0 && " • "}
                            {unitRisks.length > 0 && `${unitRisks.length} risque${unitRisks.length > 1 ? "s" : ""}`}
                          </span>
                          {(() => {
                            const rpsRisks = unitRisks.filter((r) => r.rps)
                            const phyRisks = unitRisks.filter((r) => !r.rps)
                            const worstRps =
                              rpsRisks.length > 0
                                ? rpsRisks.reduce((worst, current) =>
                                    (current.valueEffective ?? 0) > (worst.valueEffective ?? 0) ? current : worst
                                  )
                                : null
                            const worstPhy =
                              phyRisks.length > 0
                                ? phyRisks.reduce((worst, current) =>
                                    (current.valueEffective ?? 0) > (worst.valueEffective ?? 0) ? current : worst
                                  )
                                : null
                            return (
                              <>
                                {rpsRisks.length > 0 && (
                                  <RiskTypeChip
                                    isRps
                                    count={rpsRisks.length}
                                    showCount
                                    color={worstRps?.valueEffectiveColor || undefined}
                                    isSmall
                                    showLabel={false}
                                    tooltipPosition="right"
                                  />
                                )}
                                {phyRisks.length > 0 && (
                                  <RiskTypeChip
                                    isRps={false}
                                    count={phyRisks.length}
                                    showCount
                                    color={worstPhy?.valueEffectiveColor || undefined}
                                    isSmall
                                    showLabel={false}
                                    tooltipPosition="right"
                                  />
                                )}
                              </>
                            )
                          })()}
                        </div>
                        <p className="text-sm text-gray-500 italic mt-1.5 leading-tight">
                          {unit?.description || t("Aucune description")}
                        </p>
                      </td>
                    </tr>

                    {/* Risk Rows */}
                    {unitRisks.map((risk, rIdx) => {
                      const sevIdx = (organization.severityScale || []).findIndex((s) => s.value === risk.severity) ?? 0
                      const freqIdx =
                        (organization.frequencyScale || []).findIndex((s) => s.value === risk.frequency) ?? 0
                      const prevIdx =
                        (organization.preventionScale || []).findIndex((s) => s.value === risk.prevention) ?? 0

                      const sevColor = scalePositionColor(
                        sevIdx >= 0 ? sevIdx : 0,
                        (organization.severityScale || []).length || 1
                      )
                      const freqColor = scalePositionColor(
                        freqIdx >= 0 ? freqIdx : 0,
                        (organization.frequencyScale || []).length || 1
                      )
                      const prevColor = scalePositionColor(
                        prevIdx >= 0 ? prevIdx : 0,
                        (organization.preventionScale || []).length || 1
                      )

                      const bgColor = rIdx % 2 === 0 ? "white" : "#f9fafb"

                      return (
                        <tr key={risk.id} data-unit-id={unitId} style={{ backgroundColor: bgColor }}>
                          <td
                            className="px-1.5 py-1.5 border border-gray-300 text-center"
                            style={{ minWidth: "100px", maxWidth: "120px" }}>
                            <div className="flex items-center justify-center w-full h-full">
                              <RiskTypeChip
                                isRps={risk.rps}
                                showCount={false}
                                color={risk.valueEffectiveColor || undefined}
                                showLabel={false}
                                tooltipPosition="right"
                                isSmall
                              />
                            </div>
                          </td>
                          {(organization.riskAttributes || []).map((attr) => (
                            <td
                              key={attr}
                              className="px-1.5 py-1.5 border border-gray-300 text-left text-sm"
                              style={{ maxWidth: "200px" }}>
                              <div className="break-words">
                                {risk.attributes?.[attr] ? highlightSearchMatch(risk.attributes[attr]) : "—"}
                              </div>
                            </td>
                          ))}
                          <td
                            className="px-1.5 py-1.5 border border-gray-300 text-center font-bold"
                            style={{ color: sevColor, maxWidth: "80px" }}>
                            {risk.severity}
                          </td>
                          <td
                            className="px-1.5 py-1.5 border border-gray-300 text-center font-bold"
                            style={{ color: freqColor, maxWidth: "80px" }}>
                            {risk.frequency}
                          </td>
                          <td
                            className="px-1.5 py-1.5 border border-gray-300 text-center font-bold text-sm"
                            style={{ color: risk.valueInitialColor || undefined, maxWidth: "120px" }}>
                            {risk.valueInitialLabel || Math.round((risk.valueInitial ?? 0) * 10) / 10}
                          </td>
                          <td
                            className="px-1.5 py-1.5 border border-gray-300 text-center font-bold"
                            style={{ color: prevColor, maxWidth: "80px" }}>
                            {risk.prevention}
                          </td>
                          <td
                            className="px-1.5 py-1.5 border border-gray-300 text-center"
                            style={{ minWidth: "130px", maxWidth: "130px" }}>
                            {risk.valueEffectiveColor && (
                              <div
                                className="rounded px-1 py-0.5 text-white font-bold text-sm inline-block"
                                style={{ backgroundColor: risk.valueEffectiveColor }}>
                                {risk.valueEffectiveLabel || Math.round((risk.valueEffective ?? 0) * 10) / 10}
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fixed action bar - centered */}
      <motion.div
        className="fixed bottom-2 flex gap-1.5 z-50 pointer-events-none items-center"
        style={{ left: "50%", x: "-50%" }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        data-action-bar>
        <div className="bg-white rounded-lg shadow-lg flex gap-2 items-center justify-center pointer-events-auto p-2">
          {/* Left scroll controls */}
          <div className="flex gap-2 items-center">
            <Tooltip text={t("Début du tableau")}>
              <motion.button
                disabled={!canScrollLeft}
                className="list-button grey-t disabled:cursor-not-allowed text-sm pointer-events-auto"
                onClick={() => animateScrollTo(0, 500)}
                whileHover={canScrollLeft ? { scale: 1.1 } : {}}
                whileTap={canScrollLeft ? { scale: 0.95 } : {}}
                animate={{ opacity: canScrollLeft ? 1 : 0.5 }}
                transition={{ duration: 0.2 }}>
                <FontAwesomeIcon icon={faBackward} />
              </motion.button>
            </Tooltip>
            <Tooltip text={t("Défiler à gauche")}>
              <motion.button
                disabled={!canScrollLeft}
                className="list-button grey-t disabled:cursor-not-allowed pointer-events-auto text-base"
                onMouseDown={() => startScroll("left")}
                onMouseUp={stopScroll}
                onMouseLeave={stopScroll}
                whileHover={canScrollLeft ? { scale: 1.1 } : {}}
                whileTap={canScrollLeft ? { scale: 0.95 } : {}}
                animate={{ opacity: canScrollLeft ? 1 : 0.5 }}
                transition={{ duration: 0.2 }}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </motion.button>
            </Tooltip>
          </div>

          {/* Center: Home and Search */}
          <div className="flex items-center gap-2">
            <Tooltip text={t("Réinitialiser la vue")}>
              <motion.button
                className="list-button grey-t pointer-events-auto text-base"
                onClick={() => {
                  onSearch(null)
                  scrollToTableTop()
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}>
                <FontAwesomeIcon icon={faHome} />
              </motion.button>
            </Tooltip>
            {!isShareMode && <RisksDateSelector isCompact />}
            <div className="flex items-center bg-gray-100 rounded px-2 py-1">
              <input
                type="text"
                placeholder={t("Rechercher")}
                value={search.value || ""}
                onChange={(e) => onSearch(e.target.value)}
                className="text-sm border-0 outline-0 bg-transparent placeholder-gray-400 flex-1 min-w-0"
              />
              {search.value && (
                <button
                  onClick={() => onSearch(null)}
                  className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0 ml-1 bg-transparent border-0 p-0 cursor-pointer">
                  <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                </button>
              )}
            </div>
          </div>

          {/* Right scroll controls */}
          <div className="flex gap-2 items-center">
            <Tooltip text={t("Défiler à droite")}>
              <motion.button
                disabled={!canScrollRight}
                className="list-button grey-t disabled:cursor-not-allowed pointer-events-auto text-base"
                onMouseDown={() => startScroll("right")}
                onMouseUp={stopScroll}
                onMouseLeave={stopScroll}
                whileHover={canScrollRight ? { scale: 1.1 } : {}}
                whileTap={canScrollRight ? { scale: 0.95 } : {}}
                animate={{ opacity: canScrollRight ? 1 : 0.5 }}
                transition={{ duration: 0.2 }}>
                <FontAwesomeIcon icon={faChevronRight} />
              </motion.button>
            </Tooltip>
            <Tooltip text={t("Fin du tableau")}>
              <motion.button
                disabled={!canScrollRight}
                className="list-button grey-t disabled:cursor-not-allowed text-base pointer-events-auto"
                onClick={() => {
                  if (tableScrollRef.current) {
                    animateScrollTo(tableScrollRef.current.scrollWidth, 500)
                  }
                }}
                whileHover={canScrollRight ? { scale: 1.1 } : {}}
                whileTap={canScrollRight ? { scale: 0.95 } : {}}
                animate={{ opacity: canScrollRight ? 1 : 0.5 }}
                transition={{ duration: 0.2 }}>
                <FontAwesomeIcon icon={faForward} />
              </motion.button>
            </Tooltip>
          </div>

          {/* Export button */}
          {!isShareMode && (
            <Tooltip text={t("Exporter le document")}>
              <motion.button
                onClick={() => setIsExportModalOpen(true)}
                className="list-button grey-t pointer-events-auto text-base"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}>
                <FontAwesomeIcon icon={faDownload} />
              </motion.button>
            </Tooltip>
          )}

          {/* Units menu button */}
          <div className="relative">
            <Tooltip text={t("Unités de travail")}>
              <motion.button
                onClick={() => setIsUnitsMenuOpen(!isUnitsMenuOpen)}
                className="list-button grey-t pointer-events-auto text-base"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}>
                <FontAwesomeIcon icon={faBars} />
              </motion.button>
            </Tooltip>

            {/* Units dropdown menu */}
            {isUnitsMenuOpen && (
              <motion.div
                className="absolute right-0 bottom-full mb-2 bg-white rounded-lg shadow-lg z-50 overflow-y-auto border border-gray-200 w-48"
                style={{ maxHeight: "420px" }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}>
                {Array.from(byUnit.keys()).map((unitId) => {
                  const unit = units.find((u) => u.id === unitId)
                  return (
                    <ListItem
                      key={unitId}
                      onClick={() => {
                        scrollToTableTop()
                        scrollToUnit(unitId)
                      }}
                      className="text-sm cursor-pointer px-4 py-2 hover:bg-gray-100"
                      isSmall>
                      {unit?.name || unitId}
                    </ListItem>
                  )
                })}
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}
